import React, { useState } from "react"
import Layout from "../components/common/Layout"
import { ClientOnly } from "../components/common"
import { MetaData as MetaTags } from "../components/common/meta"
import FirebaseProvider from "../components/idealline/Firebase"
import AlfanoImport from "../components/idealline/AlfanoImport"
import Notification from "../components/common/Notification"
import TelemetryLandingPage from "../components/landing/TelemetryLandingPage"
import { onImportLine } from "../utils/lineUtils"
import { trackEvent } from "../utils/analytics"

const AlfanoImporterPage = ({ location }) => {
    const [importError, setImportError] = useState(null)
    const [importWarning, setImportWarning] = useState(null)
    const [triggerOpen, setTriggerOpen] = useState(false)

    const openImportModal = () => {
        setTriggerOpen(true)
        setTimeout(() => setTriggerOpen(false), 100)
    }

    const handleImport = async (points, carInfo, telemetryId) => {
        setImportError(null)
        setImportWarning(null)

        if (!points) {
            setImportError(
                carInfo?.message ||
                    "Failed to extract GPS data from the Alfano file."
            )
            return
        }

        try {
            const result = await onImportLine({ points, carInfo, telemetryId })
            if (!result.lineId) {
                trackEvent("telemetry_import_error", {
                    product: "alfano",
                    placement: "importer",
                    stage: "line_creation",
                    error_code: "line_creation_failed",
                })
                setImportError(
                    "Failed to create the racing line. Please try again."
                )
                return
            }
            if (result.warning) setImportWarning(result.warning)
            trackEvent("telemetry_import_complete", {
                product: "alfano",
                placement: "importer",
                stage: "line_creation",
            })
            window.location.href = `/telemetry-report/?id=${encodeURIComponent(telemetryId)}&lineUrl=${encodeURIComponent(result.url)}`
        } catch (error) {
            trackEvent("telemetry_import_error", {
                product: "alfano",
                placement: "importer",
                stage: "line_creation",
                error_code: "line_creation_failed",
            })
            setImportError(
                error.message || "The import could not be completed."
            )
        }
    }

    return (
        <Layout>
            <MetaTags
                title="Alfano Data Analysis for Karting | Online Coaching Report"
                description="Analyze Alfano 6 and 7 karting data online. Compare GPS lines, speed, RPM and lap sections in 3D, then take clear coaching priorities into your next run."
                location={location}
            />
            <TelemetryLandingPage
                provider="Alfano"
                providerKey="alfano"
                eyebrow="Alfano data analysis for karting"
                title="Turn Alfano data into"
                accentTitle="a faster next run"
                lede="Compare GPS lines, speed, RPM and lap sections from your Alfano 6 or Alfano 7. Open Racer shows which approach worked, what stayed inconsistent and what to test when you go back out."
                sampleHref="/alfano-coaching-report/"
                sampleTitle="KZ2 at Kartódromo do Algarve"
                sampleDescription="See how the analyzer turns five measured Alfano laps into corner priorities, repeatable references and focused driver tests."
                sampleMetrics={[
                    { value: "1:00.560", label: "fastest lap" },
                    { value: "5", label: "valid laps" },
                    { value: "8", label: "analyzed corners" },
                    { value: "145.3", label: "maximum km/h" },
                ]}
                sampleHighlights={[
                    "See the measured GPS line in the real 3D circuit context",
                    "Compare speed and RPM across every recorded lap",
                    "Find the corners where a different approach produced a measurable result",
                    "Take a small number of practical tests back to the kart",
                ]}
                analysisExample={{
                    title: "Final sweeper",
                    summary:
                        "Lap 5 was 0.119 seconds quicker through the final section. It carried 91.7 km/h at the apex versus 87.0 and still reached 105.5 km/h on exit.",
                    metrics: [
                        { value: "0.119 s", label: "measured difference" },
                        { value: "+4.7 km/h", label: "apex speed" },
                        { value: "+2.7 km/h", label: "exit speed" },
                    ],
                    action: "Repeat the Lap 5 turn in and minimum speed target. Build up in 1 km/h steps and keep the steering release clean onto the straight.",
                }}
                importTitle="Import your Alfano session"
                importDescription="Export a ZIP file from the Alfano app or Visual Data, then choose it here. Open Racer detects the recorded laps and builds the racing line from the GPS samples."
                importButtonLabel="Import an Alfano session"
                onOpenImport={openImportModal}
                importExperience={
                    <ClientOnly>
                        <FirebaseProvider>
                            <AlfanoImport
                                triggerOpen={triggerOpen}
                                onImport={handleImport}
                            />
                        </FirebaseProvider>
                    </ClientOnly>
                }
                requirements={[
                    "An Alfano 6 or Alfano 7 data logger",
                    "GPS enabled during the session",
                    "A ZIP export from the Alfano app or Visual Data",
                    "At least one complete recorded lap",
                ]}
                steps={[
                    {
                        title: "Export the session",
                        description:
                            "Choose the session in the Alfano app or Visual Data and export the recorded data as a ZIP file.",
                    },
                    {
                        title: "Choose the ZIP file",
                        description:
                            "Open the importer from your phone or computer. Open Racer validates the GPS data and identifies the available laps.",
                    },
                    {
                        title: "Review what matters",
                        description:
                            "Study the 3D line, lap trace and corner priorities, then return to the track with one clear test.",
                    },
                ]}
                features={[
                    {
                        title: "Built around the lap",
                        description:
                            "GPS, speed and RPM stay connected to the circuit instead of becoming separate tables to interpret.",
                    },
                    {
                        title: "Works from the paddock",
                        description:
                            "The ZIP import works from a phone, tablet or computer after the session has been shared from Alfano.",
                    },
                    {
                        title: "Priorities, not noise",
                        description:
                            "The report highlights a small number of corner groups and practical observations for the next run.",
                    },
                ]}
                faqs={[
                    {
                        question: "How do I export the Alfano session?",
                        answer: "Open the session in the Alfano app and use the share action to export a ZIP file. Visual Data can also export the same session data from a computer.",
                    },
                    {
                        question: "Does this replace Alfano Data Analysis (ADA)?",
                        answer: "No. Alfano ADA is the official software for managing and analyzing Alfano data. Open Racer uses an exported ZIP to add an online 3D racing line, corner-by-corner comparisons and coaching priorities for the next run.",
                    },
                    {
                        question: "Can I inspect a report before subscribing?",
                        answer: "Yes. The complete KZ2 Algarve sample report is public and can be explored without an account or Paddock Pass.",
                    },
                    {
                        question: "What requires Paddock Pass?",
                        answer: "Creating a new racing line from your own Alfano ZIP requires Paddock Pass from the first import. Viewing the public sample remains free.",
                    },
                    {
                        question: "Can I import from my phone?",
                        answer: "Yes. You can export the ZIP from the Alfano app and open the importer on the same phone or tablet.",
                    },
                ]}
            />
            <div className="fixed left-1/2 top-32 z-[9999] w-full max-w-sm -translate-x-1/2 space-y-2">
                {importError && (
                    <Notification
                        message={importError}
                        type="error"
                        onClose={() => setImportError(null)}
                    />
                )}
                {importWarning && (
                    <Notification
                        message={importWarning}
                        type="warning"
                        onClose={() => setImportWarning(null)}
                    />
                )}
            </div>
        </Layout>
    )
}

export default AlfanoImporterPage
